import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { MapPin, Navigation, Tent, Car, DoorOpen, Store, UtensilsCrossed, Bath, Cross, Ticket, Circle } from "lucide-react";

const VENUE = "Henry County Fairgrounds, 1106 Castle Highway, Eminence, KY 40019";

const CATS = {
  Arena: { icon: Tent, color: "#0F2040", bg: "bg-[#0F2040]" },
  Parking: { icon: Car, color: "#2563eb", bg: "bg-blue-600" },
  Entrance: { icon: DoorOpen, color: "#16a34a", bg: "bg-green-600" },
  Vendors: { icon: Store, color: "#C27A45", bg: "bg-secondary" },
  Food: { icon: UtensilsCrossed, color: "#d97706", bg: "bg-amber-600" },
  Restrooms: { icon: Bath, color: "#7c3aed", bg: "bg-violet-600" },
  "First Aid": { icon: Cross, color: "#dc2626", bg: "bg-red-600" },
  Ticketing: { icon: Ticket, color: "#0891b2", bg: "bg-cyan-600" },
  Other: { icon: Circle, color: "#64748b", bg: "bg-slate-500" },
};
const catOf = (c) => CATS[c] || CATS.Other;

const openDirections = () => {
  const q = encodeURIComponent(VENUE);
  const ua = navigator.userAgent || "";
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${q}`;
  let nativeUrl = null;
  if (/iPhone|iPad|iPod/i.test(ua)) nativeUrl = `maps://maps.apple.com/?daddr=${q}`;
  else if (/Android/i.test(ua)) nativeUrl = `geo:0,0?q=${q}`;
  if (nativeUrl) { window.location.href = nativeUrl; setTimeout(() => window.open(gmaps, "_blank"), 700); }
  else window.open(gmaps, "_blank");
};

export default function EventMap() {
  const [locations, setLocations] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    api.get("/map_locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const cats = useMemo(() => [...new Set(locations.map((l) => l.category || "Other"))], [locations]);
  const shown = useMemo(() => (filter ? locations.filter((l) => (l.category || "Other") === filter) : locations), [locations, filter]);
  const selected = locations.find((l) => l.id === active);

  return (
    <AppLayout>
      <PageHeader subtitle="Find Your Way" title="Event Map" />

      <div className="px-4 pt-2" data-testid="event-map-page">
        <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
          <MapPin size={18} className="mt-0.5 shrink-0 text-secondary" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary">Henry County Fairgrounds</p>
            <p className="text-xs text-muted-foreground">1106 Castle Highway, Eminence, KY 40019</p>
          </div>
          <button data-testid="map-directions-btn" onClick={openDirections}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground active:scale-95">
            <Navigation size={15} /> Directions
          </button>
        </div>

        {/* Real venue map */}
        <div className="mt-3 overflow-hidden rounded-xl border border-border">
          <iframe title="Venue location" data-testid="venue-map-embed" className="h-52 w-full"
            loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(VENUE)}&z=15&output=embed`} />
        </div>

        {locations.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-border bg-card/50 p-6 text-center">
            <MapPin size={30} className="mx-auto text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground" data-testid="map-empty">
              The on-site grounds layout with arenas, parking, and vendor areas will be posted here before the event.
            </p>
          </div>
        ) : (
          <>
            {/* Category filter */}
            <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1">
              <button data-testid="map-filter-all" onClick={() => setFilter(null)}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold ${!filter ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>All</button>
              {cats.map((c) => {
                const Icon = catOf(c).icon;
                return (
                  <button key={c} data-testid={`map-filter-${c}`} onClick={() => setFilter(filter === c ? null : c)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold ${filter === c ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>
                    <Icon size={13} /> {c}
                  </button>
                );
              })}
            </div>

            {/* Schematic grounds with pins */}
            <div data-testid="map-schematic"
              className="relative mt-3 aspect-[4/3] w-full overflow-hidden rounded-xl border border-border"
              style={{ background: "linear-gradient(135deg,#eef3ea,#e3ece0)", backgroundImage: "linear-gradient(135deg,#eef3ea,#e3ece0), repeating-linear-gradient(0deg,transparent,transparent 23px,rgba(15,32,64,.05) 24px), repeating-linear-gradient(90deg,transparent,transparent 23px,rgba(15,32,64,.05) 24px)" }}>
              {shown.map((l) => {
                const { icon: Icon, bg } = catOf(l.category);
                const isSel = l.id === active;
                return (
                  <button key={l.id} data-testid={`map-pin-${l.id}`} onClick={() => setActive(isSel ? null : l.id)}
                    className={`absolute -translate-x-1/2 -translate-y-full transition-transform ${isSel ? "z-20 scale-110" : "z-10"}`}
                    style={{ left: `${l.x ?? 50}%`, top: `${l.y ?? 50}%` }}>
                    <span className={`grid h-8 w-8 place-items-center rounded-full text-white shadow-md ring-2 ring-white ${bg}`}>
                      <Icon size={15} />
                    </span>
                    <span className={`mx-auto block h-0 w-0 border-x-4 border-t-4 border-x-transparent`} style={{ borderTopColor: catOf(l.category).color }} />
                    {isSel && (
                      <span className="absolute left-1/2 top-9 w-max max-w-[140px] -translate-x-1/2 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground shadow-lg">
                        {l.name}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">Tap a pin to see details. Layout is a guide and not to scale.</p>

            {selected && (
              <div data-testid="map-detail" className="mt-3 rounded-xl border border-secondary/40 bg-secondary/5 p-4">
                <div className="flex items-center gap-2">
                  <span className={`grid h-8 w-8 place-items-center rounded-full text-white ${catOf(selected.category).bg}`}>
                    {(() => { const I = catOf(selected.category).icon; return <I size={15} />; })()}
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold text-primary">{selected.name}</p>
                    <p className="text-xs font-bold uppercase tracking-wider text-secondary">{selected.category}</p>
                  </div>
                </div>
                {selected.description && <p className="mt-2 text-sm text-foreground/80">{selected.description}</p>}
              </div>
            )}

            {/* List */}
            <div className="mt-4 space-y-2">
              {shown.map((l) => {
                const { icon: Icon, bg } = catOf(l.category);
                return (
                  <button key={l.id} data-testid={`map-list-${l.id}`} onClick={() => setActive(l.id)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left shadow-sm active:scale-[.99]">
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-white ${bg}`}><Icon size={16} /></span>
                    <div className="flex-1">
                      <p className="font-semibold text-primary">{l.name}</p>
                      {l.description && <p className="text-xs text-muted-foreground">{l.description}</p>}
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{l.category}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
